import React, { Fragment, useState } from "react";
import * as PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
	FormControl,
	FormHelperText,
	InputLabel,
	TextField
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import cx from "classnames";
import get from "lodash/get";

import colors from "../../../../utils/colors";
import theme from "../../../theme";

const useStyles = makeStyles({
	cssFocused: {
		"& svg": {
			color: colors.octaBlue
		}
		// transform: "translate(24px, -6px) scale(1)"
	},
	inputAction: {
		width: "50%"
	},
	cssOutlinedInput: {
		borderRadius: "40px",
		borderColor: colors.white,
		// padding: "7px 23px !important",
		padding: "0px !important",
		backgroundColor: colors.inputBackground,
		fontWeight: 500,
		fontSize: "14px",
		color: colors.octaBlue,
		"&.Mui-focused": {
			border: `1px solid ${colors.borderLight}`,
			"&:hover": {
				borderColor: colors.octaBlue
			}
		},
		"& fieldset": {
			borderRadius: "40px",
			borderColor: colors.white
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			// borderColor: colors.octaBlue
		},
		"& input": {
			padding: `17px 22px 15px !important`
		},
		"& svg": {
			borderRadius: "50px",
			background: colors.gray,
			color: colors.white,
			fontSize: 40,
			padding: theme.spacing(1)
		}
	},
	cssLabel: {
		padding: "5px 0",
		color: "rgb(31 32 34)",
		fontWeight: "bold",
		fontSize: "14px",
		lineHeight: 1.25,
		position: "relative",
		transform: "none",
		"&.Mui-focused": {
			color: colors.octaBlue
		}
	},
	labelOutlined: {
		transform: "translate(25px, 10px) scale(1)",
		"&.Mui-focused": {
			// transform: "translate(24px, -6px) scale(1)"
		}
	},
	formHelperRoot: {
		fontSize: "10px",
		textTransform: "none",
		marginTop: "4px",
		fontWeight: "normal"
	},
	inputSpacing: {
		marginTop: "4px"
	},
	cssOutline: {
		borderRadius: "40px",
		borderColor: colors.white,
		"&:hover": {
			borderColor: `${colors.octaBlue}`
		}
	},
	option: {
		fontSize: 15,
		"& > span": {
			marginRight: 10,
			fontSize: 18
		}
	},
	hide: {
		display: "none"
	},
	hoverBorder: {
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: colors.octaBlue
		}
	}
});

export const AutoComplete = (props: any) => {
	const classes = useStyles();
	const { t } = useTranslation("common");
	const [openAutoComplete, setOpenAutoComplete] = useState(false);
	const { i18n } = useTranslation();
	const {
		id,
		name,
		type,
		label,
		placeholder,
		helperText,
		value,
		defaultValue,
		optionsList,
		onChange,
		error,
		icon,
		disabled,
		readOnly,
		required,
		isCustom,
		...rest
	} = props;

	const onAutoCompleteChange = (event: any) => {
		if (!openAutoComplete && event && event.target.value.length > 2) {
			setOpenAutoComplete(true);
		}
	};

	return (
		<Autocomplete
			id="country-select-demo"
			value={value}
			onChange={onChange}
			defaultValue={defaultValue}
			options={openAutoComplete ? optionsList : []}
			clearOnEscape
			classes={{
				inputRoot: cx(classes.cssOutlinedInput, {
					[classes.hoverBorder]: disabled === false
				}),
				option: classes.option,
				noOptions: classes.hide
			}}
			autoHighlight
			getOptionLabel={(option: any) =>
				option.code !== "" ? `${option.code} - ${option.locality}` : ""
			}
			renderOption={(props: any, option: any) => (
				<li {...props}>{`${option.code} - ${option.locality}`}</li>
			)}
			filterOptions={createFilterOptions({
				matchFrom: "start"
			})}
			disabled={disabled}
			renderInput={(params) => (
				<FormControl
					variant="standard"
					classes={{
						root: cx(classes.inputSpacing)
					}}
					error={error}
					disabled={disabled}
					required={required}
					fullWidth
				>
					{label && (
						<InputLabel
							htmlFor={name}
							classes={{
								root: classes.cssLabel,
								focused: classes.cssFocused,
								outlined: classes.labelOutlined
							}}
							shrink={false}
						>
							{label}
						</InputLabel>
					)}
					<TextField
						name={name}
						variant="outlined"
						error={error}
						required={required}
						onChange={onAutoCompleteChange}
						autoFocus 
						{...params}
						inputProps={{
							...params.inputProps,
							autoComplete: "new-password", // disable autocomplete and autofill
							classes: {
								notchedOutline: classes.cssOutline,
								...get(params, "inputProps.classes", {})
							},
							inputMode: "numeric"
						}}
					/>
					{helperText && (
						<FormHelperText className={classes.formHelperRoot}>
							{helperText}
						</FormHelperText>
					)}
				</FormControl>
			)}
			disableClearable
			forcePopupIcon={false}
		/>
	);
};

AutoComplete.defaultProps = {
	type: "text",
	error: false,
	readOnly: false,
	placeholder: "",
	helperText: "",
	disabled: false,
	isCustom: false,
	required: false
};

AutoComplete.propTypes = {
	error: PropTypes.bool,
	type: PropTypes.string,
	placeholder: PropTypes.string,
	helperText: PropTypes.string,
	readOnly: PropTypes.bool,
	disabled: PropTypes.bool,
	isCustom: PropTypes.bool,
	required: PropTypes.bool,
	icon: PropTypes.element,
	id: PropTypes.string.isRequired,
	name: PropTypes.string,
	label: PropTypes.string.isRequired,
	optionsList: PropTypes.arrayOf(PropTypes.object).isRequired,
	value: PropTypes.object.isRequired,
	defaultValue: PropTypes.object,
	onChange: PropTypes.func.isRequired
};

export default React.memo(AutoComplete);
